import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUserID } from '../../store/actions/ChatAction';
import { startTimerApi, stopTimerApi, getTimeEntriesApi, getActiveEntriesApi, getOngoingActivitiesApi } from '../../store/actions/TimeManagementActions';
import { TimeEntry } from '../../store/reducer/TimeManagementReducer';
import { RootState, useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import { fetchAllTasks } from '../../store/actions/TasksActions';

interface TimerContextType {
  time: number;
  isRunning: boolean;
  selectedItems: any[];
  activeList: 'tasks' | 'cases';
  tasks: any[];
  timeEntries: TimeEntry[];
  startTimer: () => Promise<void>;
  stopTimer: (data: { task_ids: string[]; description: string; is_billable: boolean; TaskCodeID?: number }) => Promise<void>;
  setActiveList: (list: 'tasks' | 'cases') => void;
  handleItemClick: (item: any) => void;
  resetState: () => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [activeList, setActiveList] = useState<'tasks' | 'cases'>('tasks');
  
  const tasks = useSelector((state: RootState) => state.tasks.tasks).filter((task: any) => task.Status === 'Active');
  const timeEntries = useSelector((state: RootState) => state.time.timeEntries);
  const userId = getUserID();
  const [activeEntryId, setActiveEntryId] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const startLocalTimer = (initialSeconds: number) => {
    setTime(initialSeconds);
    setIsRunning(true);
    const id = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 1000);
    setIntervalId(id);
  };

  const stopLocalTimer = () => {
    if (intervalId) clearInterval(intervalId);
    setIntervalId(null);
    setIsRunning(false);
  };

  useEffect(() => {
    const checkActiveEntry = async () => {
      if (!userId) return;

      const activeEntries: any[] = await dispatch(getActiveEntriesApi());
      if (activeEntries && activeEntries.length > 0) {
        const activeEntry = activeEntries[0];
        setActiveEntryId(activeEntry.entry_id);

        const start_time = new Date(activeEntry.start_time).getTime();
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - start_time) / 1000);
        
        startLocalTimer(elapsedSeconds >= 0 ? elapsedSeconds : 0);
      } else {
        resetState();
      }
    };

    checkActiveEntry();
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAllTasks());
    }
  }, [dispatch, userId]);

  const startTimer = async () => {
    if (isRunning && activeEntryId) {
      return;
    }
    const timer_info: any = await dispatch(startTimerApi());
    if (timer_info && timer_info.entry_id) {
      setActiveEntryId(timer_info.entry_id);
      const startTime = new Date(timer_info.start_time).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      startLocalTimer(elapsedSeconds >= 0 ? elapsedSeconds : 0);
      await dispatch(getActiveEntriesApi());
    } else {
      console.error("Unexpected timer_info format", timer_info);
    }
  };

  const stopTimer = async (data: { task_ids: string[]; description: string; is_billable: boolean; TaskCodeID?: number; }) => {
    if (!activeEntryId) return;
    await dispatch(stopTimerApi(activeEntryId, data));
    resetState();
    if (selectedItems.length > 0) {
      const item = selectedItems[0];
      const case_id = activeList === 'cases' ? item.CaseID : undefined;
      const task_id = activeList === 'tasks' ? item.TaskID : undefined;
      await dispatch(getTimeEntriesApi(case_id, task_id));
    }
    dispatch(getActiveEntriesApi());
    dispatch(getOngoingActivitiesApi());
  };

  const resetState = () => {
    stopLocalTimer();
    setTime(0);
    setSelectedItems([]);
    setActiveEntryId(null);
  };

  const handleItemClick = async (item: any) => {
    const itemID = item.TaskID || item.CaseID;
    const isSelected = selectedItems.some((selectedItem) => {
      const selectedItemID = selectedItem.TaskID || selectedItem.CaseID;
      return selectedItemID === itemID;
    });
    setSelectedItems(isSelected ? [] : [item]);

    const case_id = activeList === 'cases' ? item.CaseID : undefined;
    const task_id = activeList === 'tasks' ? item.TaskID : undefined;
    await dispatch(getTimeEntriesApi(case_id, task_id));
  };

  return (
    <TimerContext.Provider
      value={{
        time,
        isRunning,
        selectedItems,
        activeList,
        tasks,
        timeEntries,
        startTimer,
        stopTimer,
        setActiveList,
        handleItemClick,
        resetState,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
